import { useEffect } from "react";
import CashBackBannerIcon from "@Icons/CashbackBannerIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import useTranslationHook from "hooks/useTranslationHook";
import { getTranslatedDurationValue } from "../../utils";
import styles from "./SlashedPrice.module.scss";

interface Offer {
  name: string;
  value: number | string;
}

interface SlashedPriceData {
  tenancy_id?: string;
  is_flexible: boolean;
  duration: string;
  base_rent: string;
  total_rent: string;
  total_rent_calculation: string;
  effective_total_rent: string;
  effective_base_rent: string;
  only_price: string;
  currency: string;
  total_saving: number | string;
  offers_array: Offer[];
  only_utility_charges: boolean;
  utility_charges: string;
  isMobile?: boolean;
  property_offers_total?: any;
  amber_benefits_total?: any;
}

interface SlashedPriceProps {
  slashedPrice: SlashedPriceData;
  isDesktop?: boolean;
}

const ICON_SIZE = { mobile: "16", desktop: "20" };
const CASHBACK_COLOR = "#0E9F6E";
const show2CategoryOffers = true;

// Translation key prefix
const T_PREFIX = "propertypage.roomtypesectiondynamic.roomtypecard";

const SlashedPrice = ({ slashedPrice, isDesktop = false }: SlashedPriceProps) => {
  const { t } = useTranslationHook();
  const iconSize = slashedPrice.isMobile ? ICON_SIZE.mobile : ICON_SIZE.desktop;

  useEffect(() => {
    try {
      gtagEventEmitter({
        action: "viewed_slashed_prices",
        category: PAGE.INVENTORY,
        label: "success",
        tenancy_id: slashedPrice?.tenancy_id,
      });
    } catch (error) {
      console.error("Error emitting gtag event:", error);
    }
  }, [slashedPrice?.tenancy_id]);

  const containerOneClass = formatClassNames(styles.disContainerOne, !isDesktop && styles.mobile);
  const containerThreeClass = formatClassNames(
    styles.disContainerThree,
    !isDesktop && styles.mobile,
  );

  const renderOfferItem = (offer: Offer, index: number) => (
    <div key={`offer-${index}`} className={styles.discountTableInner}>
      <span className={styles.discountTableInnerLabel}>
        <CashBackBannerIcon fill={CASHBACK_COLOR} width={iconSize} height={iconSize} />
        <span>{offer.name}</span>
      </span>
      <span className={styles.discountTableInnerValue}>
        -{slashedPrice?.currency}
        {offer?.value}
      </span>
    </div>
  );

  const renderOfferItem2Category = () => (
    <>
      {slashedPrice.property_offers_total > 0 && (
        <div className={styles.offerCategoryItem}>
          <div className={styles.offerCategoryHeader}>
            <span className={styles.offerCategoryTitle}>
              <CashBackBannerIcon fill={CASHBACK_COLOR} width={iconSize} height={iconSize} />
              <span>{t("propertypage.slashpricing.propertyoffer")}</span>
            </span>
            <span className={styles.offerCategoryValue}>
              -{slashedPrice?.currency}
              {slashedPrice?.property_offers_total}
            </span>
          </div>
          <p className={styles.offerCategoryDescription}>
            {t("propertypage.slashpricing.propertyofferdesc")}
          </p>
        </div>
      )}

      {slashedPrice.amber_benefits_total > 0 && (
        <div className={styles.offerCategoryItem}>
          <div className={styles.offerCategoryHeader}>
            <span className={styles.offerCategoryTitle}>
              <CashBackBannerIcon fill={CASHBACK_COLOR} width={iconSize} height={iconSize} />
              <span>{t("propertypage.slashpricing.amberbenefits")}</span>
            </span>
            <span className={styles.offerCategoryValue}>
              -{slashedPrice?.currency}
              {slashedPrice.amber_benefits_total}
            </span>
          </div>
          <p className={styles.offerCategoryDescription}>
            {t("propertypage.slashpricing.amberbenefitsdesc")}
          </p>
        </div>
      )}
    </>
  );

  const renderTotalSavings = () => {
    if (slashedPrice.offers_array.length <= 1) return null;

    return (
      <>
        <div className={styles.discountTableSeparator} />
        <div className={styles.discountTableInner}>
          <span className={styles.discountTableInnerLabel}>{t(`${T_PREFIX}.totalsavings`)}</span>
          <span className={styles.discountTableInnerValueTotal}>
            -{slashedPrice?.currency}
            {slashedPrice.total_saving}
          </span>
        </div>
      </>
    );
  };

  const renderOffersTable = (isFlexible: boolean) => (
    <div className={styles.disContainerTwo}>
      <div className={formatClassNames(styles.discountTable, isFlexible && styles.flexi)}>
        <div className={styles.offerBox}>{t(`${T_PREFIX}.offersapplied`)}</div>
        {show2CategoryOffers
          ? renderOfferItem2Category()
          : slashedPrice.offers_array?.map(renderOfferItem)}
        {renderTotalSavings()}
      </div>
    </div>
  );

  const renderUtilityBills = (showTranslatedValue = false) => {
    if (!slashedPrice.only_utility_charges) return null;

    const utilityValue = showTranslatedValue
      ? getTranslatedDurationValue(slashedPrice.utility_charges, t, "/") ||
        slashedPrice.utility_charges
      : slashedPrice.utility_charges;

    return (
      <div className={styles.BillsInculded}>
        <span className={styles.label}>{t(`${T_PREFIX}.includesestutilitybills`)}</span>
        <span className={styles.price}>
          <s>{utilityValue}</s> <span className={styles.green}>{t(`${T_PREFIX}.free`)}</span>
        </span>
      </div>
    );
  };

  if (slashedPrice.is_flexible) {
    return (
      <>
        <div className={containerOneClass}>
          <div className={styles.disContainerOneInner}>
            <span className={styles.disContainerOneInnerLabel}>
              {t(slashedPrice.duration)} {t(`${T_PREFIX}.rent`)}
            </span>
            <span className={styles.disContainerOneInnerValue}>{slashedPrice.base_rent}</span>
          </div>
        </div>
        {renderOffersTable(true)}
        {slashedPrice.only_utility_charges && (
          <div className={containerThreeClass}>{renderUtilityBills()}</div>
        )}
      </>
    );
  }

  return (
    <>
      <div className={containerOneClass}>
        <div className={styles.disContainerOneInner}>
          <span className={styles.disContainerOneInnerLabel}>
            {t(slashedPrice.duration)} {t(`${T_PREFIX}.rent`)}
          </span>
          <span className={styles.disContainerOneInnerValue}>
            {getTranslatedDurationValue(slashedPrice.base_rent, t, "/") || slashedPrice.base_rent}
          </span>
        </div>
        <div className={styles.disContainerOneInner}>
          <span className={styles.disContainerOneInnerLabel}>
            {t(`${T_PREFIX}.totalrent`)}{" "}
            <span className={styles.disContainerOneInnerLabelSubtext}>
              (
              {getTranslatedDurationValue(slashedPrice.total_rent_calculation, t, " ") ||
                slashedPrice.total_rent_calculation}
              )
            </span>
          </span>
          <span className={styles.disContainerOneInnerValue}>{slashedPrice.total_rent}</span>
        </div>
      </div>
      {renderOffersTable(false)}
      <div className={containerThreeClass}>
        <div className={styles.disContainerThreeInner}>
          <span className={styles.disContainerThreeInnerLabel}>
            {t(`${T_PREFIX}.effectiverentafteroffer`)}
            {/* <span className={styles.tncText}>{t(`${T_PREFIX}.tncapply`)}</span> */}
            <span className={styles.tncText}>T&Cs apply</span>
          </span>
          <span className={styles.disContainerThreeInnerValue}>
            {slashedPrice.effective_total_rent}
          </span>
        </div>
        <div className={styles.InnerContainer}>
          <span className={styles.InnerContainerInnerLabel}>
            {t(`${T_PREFIX}.effective`)} {t(slashedPrice.duration)} {t(`${T_PREFIX}.rent`)}
          </span>
          <span className={styles.InnerContainerInnerValue}>
            <span className={styles.InnerContainerInnerValueSubtext}>
              <s>{slashedPrice.only_price}</s>
            </span>
            ~
            {getTranslatedDurationValue(slashedPrice.effective_base_rent, t, "/") ||
              slashedPrice.effective_base_rent}
          </span>
        </div>
        {renderUtilityBills(true)}
      </div>
    </>
  );
};

export default wrapperHOC(SlashedPrice, {
  componentName: "SlashedPrice",
  showForChina: true,
});
